import type { Scenario } from "./types";

type MonteCarloInputs = Pick<Scenario, "assumptions" | "buildings">;

export interface MonteCarloRunDefinition {
  enabledKeys: string[];
  ranges?: Record<string, number>;
  iterations: number;
}

const RUN_DEFINITION_SEPARATOR = "\n--monte-carlo-run--\n";

/**
 * Identifies the scenario inputs consumed by Monte Carlo calculations.
 * Presentation metadata is intentionally excluded so renaming or annotating a
 * scenario does not invalidate an otherwise current simulation.
 */
export function monteCarloInputFingerprint(scenario: MonteCarloInputs): string {
  return JSON.stringify({
    assumptions: scenario.assumptions,
    buildings: scenario.buildings,
  });
}

/** Identifies both the scenario inputs and controls used for a simulation. */
export function monteCarloRunFingerprint(
  scenario: MonteCarloInputs,
  run: MonteCarloRunDefinition,
): string {
  return `${monteCarloInputFingerprint(scenario)}${RUN_DEFINITION_SEPARATOR}${JSON.stringify(run)}`;
}

/**
 * Reports whether stored results still belong to the current scenario.
 * Legacy scenario-only fingerprints remain valid for PDF inclusion, while the
 * Risk screen uses the complete run fingerprint for control-level freshness.
 */
export function monteCarloScenarioMatches(
  fingerprint: string,
  scenario: MonteCarloInputs,
): boolean {
  const scenarioFingerprint = monteCarloInputFingerprint(scenario);
  return (
    fingerprint === scenarioFingerprint ||
    fingerprint.startsWith(`${scenarioFingerprint}${RUN_DEFINITION_SEPARATOR}`)
  );
}
